import { useState } from "react";
import { toast } from "sonner";
import { Download, Lock, Github, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, downloadBlob, formatApiError } from "@/lib/api";

const errText = async (e) => {
  try { return formatApiError(JSON.parse(await e.response.data.text()).detail); } catch { return "Falha no download"; }
};

const CodeBackup = () => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/backup/code", { responseType: "blob" });
      downloadBlob(r.data, "bridge-iptv-ia-backup.zip");
      setInfo({ sha: r.headers["x-backup-sha256"], files: r.headers["x-backup-files"], size: r.data.size });
      toast.success("ZIP do código-fonte baixado");
    } catch (e) { toast.error(await errText(e)); }
    setBusy(false);
  };
  return (
    <section className="panel-card space-y-3" data-testid="code-backup">
      <div className="flex items-center gap-2"><Download className="w-4 h-4 text-[#10B981]" /><h2 className="font-heading text-base md:text-lg font-semibold">Código-fonte completo (ZIP)</h2></div>
      <p className="text-xs text-[#9CA3AF]">Frontend, backend, testes, documentação, README, .env.example, Dockerfiles e docker-compose. NÃO inclui arquivos .env, senhas, tokens, chaves, node_modules nem dados de clientes. O servidor bloqueia o download se encontrar alguma credencial nos arquivos.</p>
      <Button onClick={run} disabled={busy} className="btn-primary h-11" data-testid="download-code-button">{busy ? "Gerando..." : "Baixar bridge-iptv-ia-backup.zip"}</Button>
      {info && <p className="text-[11px] font-mono text-[#9CA3AF] break-all" data-testid="code-backup-info">{info.files} arquivos · {(info.size / 1024).toFixed(0)} KB · SHA-256 {info.sha}</p>}
    </section>
  );
};

const DataBackup = () => {
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (pw.a.length < 10) return toast.error("Use uma frase-senha com pelo menos 10 caracteres");
    if (pw.a !== pw.b) return toast.error("As frases-senha não conferem");
    setBusy(true);
    try {
      const r = await api.post("/admin/backup/data", { passphrase: pw.a }, { responseType: "blob" });
      downloadBlob(r.data, "bridge-iptv-ia-dados.enc");
      toast.success("Exportação criptografada baixada");
      setPw({ a: "", b: "" });
    } catch (e) { toast.error(await errText(e)); }
    setBusy(false);
  };
  return (
    <section className="panel-card space-y-3" data-testid="data-backup">
      <div className="flex items-center gap-2"><Database className="w-4 h-4 text-[#A78BFA]" /><h2 className="font-heading text-base md:text-lg font-semibold">Exportação dos dados (separada e criptografada)</h2></div>
      <p className="text-xs text-[#9CA3AF]">Contém dados reais (conversas, pedidos, configurações e o token do webhook). É criptografada (AES/Fernet + PBKDF2) com a frase-senha que você digitar — ela não é salva em lugar nenhum: sem ela, não é possível restaurar. Guarde o arquivo fora do GitHub.</p>
      <Input type="password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} placeholder="Frase-senha (mín. 10 caracteres)" className="input-dark h-11" data-testid="data-passphrase-input" />
      <Input type="password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} placeholder="Repita a frase-senha" className="input-dark h-11" data-testid="data-passphrase-confirm-input" />
      <Button onClick={run} disabled={busy} className="btn-ghost h-11" variant="outline" data-testid="download-data-button"><Lock className="w-4 h-4 mr-2" />{busy ? "Gerando..." : "Baixar dados criptografados"}</Button>
    </section>
  );
};

export const BackupPanel = () => (
  <div className="grid lg:grid-cols-2 gap-6 items-start">
    <CodeBackup />
    <DataBackup />
    <section className="panel-card space-y-2 lg:col-span-2" data-testid="github-guide">
      <div className="flex items-center gap-2"><Github className="w-4 h-4" /><h2 className="font-heading text-base md:text-lg font-semibold">GitHub privado</h2></div>
      <ol className="text-sm text-[#D1D5DB] list-decimal pl-5 space-y-1">
        <li>Crie no GitHub um repositório <b>Private</b> (ex.: bridge-iptv-ia).</li>
        <li>No chat do Emergent: botão <b>Save</b> → <b>Save to GitHub</b> → escolha sua conta e o repositório privado → branch → <b>Save to GitHub</b>.</li>
        <li>Arquivos .env não são enviados (estão no .gitignore). Ou: extraia o ZIP acima e rode <span className="font-mono">git init &amp;&amp; git remote add origin … &amp;&amp; git push</span>.</li>
      </ol>
      <p className="text-xs text-[#6B7280]">Instruções completas de hospedagem e restauração: docs/HOSPEDAGEM.md dentro do ZIP.</p>
    </section>
  </div>
);
